/*
 * Org-branding client — settings' resolved branding read.
 *
 *   GET {VITE_SETTINGS_API_BASE}/api/org-units/:orgUnitId/branding/resolved
 *
 * Contract per dispatch BOARDROOM-2026-08-28-110128 (settings' delivered
 * contract SETTINGS-2026-08-28-105133). Auth is the consumer-pool JWT Bearer —
 * the SAME dual-issuer gate as the participant-config / waiver-template
 * resolved reads (participantConfig.ts / waiverTemplate.ts); the staff
 * GET /brand would 401 this pool, so only this resolved read is used.
 *
 * Honest-empty: every field is null when the org unit has not configured it —
 * consumers render a NEUTRAL fallback. An accent is only ever applied when it
 * parses as a #hex colour (never inject arbitrary wire strings into CSS), and
 * it is identity-only: teal/amber status hues stay RESERVED (README §Status
 * colours). Theming is limited by design to logo + accent + hero image.
 */

export interface OrgBrandingResolved {
  orgUnitId: string
  /** #hex accent, validated — the kicker colour. */
  organiserAccent: string | null
  organiserLogoUrl: string | null
  organiserIconUrl: string | null
  /** Desktop hero (ticket-stub banner); mobile variant separate. */
  heroImageUrl: string | null
  heroImageUrlMobile: string | null
  brandFont: string | null
}

/** Wire shape (adapts here if settings ships differently). */
interface ResolvedBrandingResponse {
  org_unit_id?: string
  organiserAccent?: string | null
  organiserLogoUrl?: string | null
  organiserIconUrl?: string | null
  heroImageUrl?: string | null
  heroImageUrlMobile?: string | null
  brandFont?: string | null
}

export function orgBrandingApiBase(env: Record<string, string | undefined> = import.meta.env): string | null {
  return env.VITE_SETTINGS_API_BASE || null
}

const HEX_COLOUR_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/

/** The endpoint 400s on non-UUID keys — never call it with a synthesised
 *  `org-{name}` fallback id (api.ts mints those when the wire has no
 *  organiserId). */
export function isOrgUnitUuid(id: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)
}

const asNullableString = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null)

/** Map the wire shape → the typed contract. Pure — unit-tested. A malformed
 *  accent degrades to null (neutral fallback), never to a CSS injection. */
export function mapResolvedBranding(orgUnitId: string, json: ResolvedBrandingResponse): OrgBrandingResolved {
  const accentRaw = asNullableString(json.organiserAccent)
  return {
    orgUnitId,
    organiserAccent: accentRaw && HEX_COLOUR_RE.test(accentRaw) ? accentRaw : null,
    organiserLogoUrl: asNullableString(json.organiserLogoUrl),
    organiserIconUrl: asNullableString(json.organiserIconUrl),
    heroImageUrl: asNullableString(json.heroImageUrl),
    heroImageUrlMobile: asNullableString(json.heroImageUrlMobile),
    brandFont: asNullableString(json.brandFont),
  }
}

/** Fetch resolved branding for one org unit. Returns null on 404 (org unit
 *  unknown — honest absence, render neutral); throws on other failures so the
 *  caller can decide (DataProvider treats branding as best-effort). */
export async function fetchOrgBranding(
  orgUnitId: string,
  base: string,
  idToken: string,
  fetchImpl: typeof fetch = fetch,
): Promise<OrgBrandingResolved | null> {
  const url = `${base.replace(/\/$/, '')}/api/org-units/${encodeURIComponent(orgUnitId)}/branding/resolved`
  const res = await fetchImpl(url, { headers: { Authorization: `Bearer ${idToken}` } })
  if (res.status === 404) return null
  if (!res.ok) throw new Error(`org-branding resolved fetch failed (${res.status})`)
  return mapResolvedBranding(orgUnitId, (await res.json()) as ResolvedBrandingResponse)
}
